import type {
  DeIdentificationCandidate,
  DeIdentificationRule,
  DeIdentificationRuleConflictStatus,
  DeIdentificationRuleSourceType,
  DeIdentificationSettings,
} from '../../entities/ai-assist-config';

export interface RuleAssemblyResult {
  activeRules: DeIdentificationRule[];
  manualRules: DeIdentificationRule[];
  blockedRules: DeIdentificationRule[];
}

export function assembleDeidentificationRules(settings: DeIdentificationSettings): RuleAssemblyResult {
  const candidateRules = settings.candidates.flatMap(createCandidateRule);
  const manualRules = settings.manualRules.map((rule) => ({ ...rule }));
  const ruleCandidates = [...candidateRules, ...manualRules];
  const conflictMap = buildConflictMap(ruleCandidates.filter((rule) => rule.enabled));
  const rules = ruleCandidates.map((rule) => ({
    ...rule,
    conflictStatus: conflictMap.has(rule.ruleId) ? 'blocked-conflict' as const : 'valid' as const,
  }));
  const activeRules = rules.filter((rule) => rule.enabled && rule.conflictStatus === 'valid').sort(compareRules);
  const normalizedManualRules = rules.filter((rule) => rule.sourceType === 'manual');
  const blockedRules = rules.filter((rule) => rule.conflictStatus === 'blocked-conflict');

  return {
    activeRules,
    manualRules: normalizedManualRules,
    blockedRules,
  };
}

export function reconcileManualRules(settings: DeIdentificationSettings): DeIdentificationSettings {
  const assembly = assembleDeidentificationRules(settings);
  return {
    ...settings,
    manualRules: assembly.manualRules,
    updatedAt: new Date().toISOString(),
  };
}

export function createManualRule(input: { originalValue: string; replacementValue: string; enabled?: boolean }, now = new Date().toISOString()): DeIdentificationRule {
  return {
    ruleId: `manual:${Math.random().toString(36).slice(2, 10)}`,
    sourceType: 'manual',
    originalValue: input.originalValue.trim(),
    replacementValue: input.replacementValue.trim(),
    enabled: input.enabled ?? true,
    conflictStatus: 'valid',
    candidateId: null,
    createdAt: now,
    updatedAt: now,
  };
}

function createCandidateRule(candidate: DeIdentificationCandidate): DeIdentificationRule[] {
  if (!candidate.enabled || !candidate.replacementValue?.trim()) {
    return [];
  }

  return [
    {
      ruleId: `candidate:${candidate.candidateId}`,
      sourceType: candidate.candidateType === 'human-name' && !candidate.isCustomized ? 'human-default' : 'candidate-override',
      originalValue: candidate.originalValue,
      replacementValue: candidate.replacementValue.trim(),
      enabled: true,
      conflictStatus: 'valid',
      candidateId: candidate.candidateId,
      createdAt: new Date(0).toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ];
}

function buildConflictMap(rules: DeIdentificationRule[]): Set<string> {
  const replacementGroups = new Map<string, DeIdentificationRule[]>();
  for (const rule of rules) {
    const bucket = replacementGroups.get(rule.replacementValue) ?? [];
    bucket.push(rule);
    replacementGroups.set(rule.replacementValue, bucket);
  }

  const conflicts = new Set<string>();
  for (const group of replacementGroups.values()) {
    const originals = new Set(group.map((rule) => rule.originalValue));
    if (originals.size <= 1) {
      continue;
    }

    group.forEach((rule) => conflicts.add(rule.ruleId));
  }

  return conflicts;
}

function compareRules(left: DeIdentificationRule, right: DeIdentificationRule): number {
  return right.originalValue.length - left.originalValue.length
    || left.ruleId.localeCompare(right.ruleId);
}
